import '@progress/kendo-react-inputs';
import '@progress/kendo-theme-default/dist/all.css';

import moment from 'moment';
import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import 'react-loading-skeleton/dist/skeleton.css';
import { AuthProvider, useAuth } from 'react-oidc-context';
import { BrowserRouter, Route, Routes } from 'react-router-dom';

import { BenefitApp } from './apps/benefit/BenefitApp';
import GdprPage from './apps/benefit/Pages/GdprPage';
import { PublicInformationRoute } from './apps/benefit/Pages/InformationPage/PublicInformationRoute';
import { PUBLIC_INFORMATION_ROUTES } from './apps/benefit/Pages/InformationPage/publicInformationRoutes';
import { MerchantsApp } from './apps/merchants/MerchantsApp';
import {
  benefitOidcConfig,
  merchantsOidcConfig,
} from './common/auth/oidcConfig';
import { AutoconnectSession } from './common/auth/AutoconnectSession';
import { appTypes } from './common/constants';
import { config } from './common/global/config';
import { initAnalyticsInterceptor } from './common/piano/analytics-setup';
import './common/styles/style.scss';
import { AppInsightsErrorBoundary } from './common/telemetry/AppInsightsErrorBoundary';
import { usePageTracking } from './common/telemetry/appInsights';
import Loader from './common/widgets/Loader';
import LoadingSpinner from './common/widgets/LoadingSpinner';

initAnalyticsInterceptor();

const defaultLocale =
  window.localStorage.getItem('language') ?? config.defaultLanguage ?? 'ro';

moment.locale(defaultLocale);

const SigninOidc = () => {
  const auth = useAuth();

  useEffect(() => {
    const timeout = setTimeout(() => {
      console.warn('OIDC callback failed. Redirecting to login...');

      sessionStorage.clear();
      globalThis.location.href = '/';
    }, 30000);

    return () => clearTimeout(timeout);
  }, []);

  return auth.isAuthenticated ? null : (
    <Loader loading={true} loader={<LoadingSpinner size="large" />}>
      <></>
    </Loader>
  );
};

let app: React.ReactNode = null;

if (config.appType === appTypes.benefit) {
  app = (
    <AuthProvider {...benefitOidcConfig}>
      <Routes>
        <Route path="/signin-oidc" element={<SigninOidc />} />
        <Route
          path="/*"
          element={
            <AutoconnectSession>
              <BenefitApp />
            </AutoconnectSession>
          }
        />
      </Routes>
    </AuthProvider>
  );
} else if (config.appType === appTypes.merchants) {
  app = (
    <AuthProvider {...merchantsOidcConfig}>
      <MerchantsApp />
    </AuthProvider>
  );
}

function AppContent() {
  usePageTracking();

  return <RootApp />;
}

const RootApp = () => (
  <Routes>
    {PUBLIC_INFORMATION_ROUTES.map(({ path, type }) => (
      <Route
        key={path}
        path={path}
        element={<PublicInformationRoute type={type} />}
      />
    ))}

    <Route path="exercitare-drepturi-gdpr" element={<GdprPage />} />

    <Route path="*" element={app} />
  </Routes>
);

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement,
);

root.render(
  <React.StrictMode>
    <HelmetProvider>
      <BrowserRouter>
        <AppInsightsErrorBoundary>
          <AppContent />
        </AppInsightsErrorBoundary>
      </BrowserRouter>
    </HelmetProvider>
  </React.StrictMode>,
);
